import React, { useCallback, useState } from 'react';
import { RouteComponentProps, useHistory, withRouter, Link as RLink, useLocation } from 'react-router-dom';
import { compose } from 'recompose';
import { equals, includes, isEmpty, not, pathOr, toLower, trim } from 'ramda';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch } from 'react-redux';

// Mui
import { useTheme, useMediaQuery } from '@mui/material';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Checkbox from '@mui/material/Checkbox';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Button from 'src/components/Buttons/Button';

// services
import {
    verifyOTP,
    authenticateUserInNamespace,
    createMagicLink
} from 'src/services/account';
import { getLoginErrorData, handleLoginError, rememberMeRedirect } from 'src/features/Auth/Login/loginAccounts';
import { getQueryParam } from 'src/utilities/queryParams';
import { handleStartSession } from 'src/store/authentication/authentication.actions';
// types
import { Login as ILogin } from 'src/features/Auth/Login/LoginInterface';
import { ThunkDispatch } from 'src/store/types';

// helpers
import { isEmptyOrNil, isNotEmpty, returnIsNotEmptyOrTrue } from 'src/utilities/hasValue';
import CookieLocalStorage from 'src/features/Auth/Login/CookieLocalStorage';
import useNotification from 'src/features/ToastNotifications/ToastNotifications';

// style
import { FormContainer, FormControl } from 'src/features/Auth/Auth/Auth.styles';
import AuthenticationManager from 'src/features/Auth/Login/AuthenticationManager';
import { EMAIL_VALIDATION_REGEX } from 'src/utilities/emailValidator';
import { MIN_MODERATE_STRONG, MIN_STRONG } from 'src/utilities/notifications/accounts';
import { APP_ROOT, FORGOT_URL } from 'src/route_constants';
import { useStyles } from 'src/features/Auth/Login/Login.styles';
import MagicLinkModal from 'src/features/Auth/Login/MagicLinkModal';
import OTPVerification from 'src/features/Auth/Login/OTPVerification';
import SiteSettings from 'src/siteSetting';
import { unAuthenticatedUser } from 'src/utilities/storage';
import { useSessionHook } from 'src/hooks/useSessionHook';
import AccountLockedModal from '../Login/AccountLockedModal';
import { ACCOUNT_LOCKED } from 'src/utilities/constants';

interface Props {
    isMagicLinkEnabled: boolean;
    activeIntent?: any;
    userEmail?: string;
}
type CombinedProps = RouteComponentProps<{}> & Props;

type ErrorResponseType = {
    status: string
    code: string
    message: string
    reason: string
}

const LoginContainer: React.FC<CombinedProps> = (props) => {
    const { isMagicLinkEnabled, activeIntent, userEmail = '' } = props;
    const history = useHistory();
    const dispatch: ThunkDispatch = useDispatch();
    const [magicLink, setMagicLink] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    //const [googleSubmitting, setGoogleSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [selectMagicLink, setSelectMagicLink] = useState(false);
    const [selectMFA, setSelectMFA] = useState(false);
    const [userDetails, setUserDetails] = useState<any>(null);
    const [sendNotification] = useNotification();
    const [accountLockedData, setAccountLockedData] = useState({
        show: false,
        message: ""
    })
    const classes = useStyles();
    const { breakpoints } = useTheme();
    const matchesMd = useMediaQuery(breakpoints.down('md'));
    const matchesLg = useMediaQuery(breakpoints.down('lg'));
    const location = useLocation();

    const strongMinCharPassword = returnIsNotEmptyOrTrue(
        includes('internal', pathOr("", ['pathname'], location)),
        MIN_STRONG, MIN_MODERATE_STRONG);
    const { workFlow } = useSessionHook()
    const {
        handleSubmit,
        control,
        formState: { errors },
    } = useForm<ILogin>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            email: userEmail,
            password: '',
            rememberMe: false,
        },
    });


    const handleLoginErrorReason = (data: ErrorResponseType, cb: () => void) => {

        const reason = pathOr("", ["reason"], data)
        if (not(equals(reason, ACCOUNT_LOCKED))) {
            cb()
            return
        }

        const message = pathOr("Please reset your password and try again", ["message"], data)
        setAccountLockedData({
            show: true,
            message
        })
    }


    const redirectMeta = getQueryParam(location.search, 'returnTo');

    const returnTo = isNotEmpty(redirectMeta) ? String(window.atob(redirectMeta)) : '';

    // Checks is User has enabled Remember me on browser session
    if (CookieLocalStorage.isUserRemembered()) {
        rememberMeRedirect(
            CookieLocalStorage.getUser(),
            returnTo,
            history
        );
    }

    const onSubmit = useCallback(async (data: ILogin, submitType?: any) => {
        const { email: userEmail, password, rememberMe } = data;
        const email = toLower(trim(userEmail))
        //Here clean up old session storage

        isEmpty(activeIntent) && await dispatch(
            handleStartSession({
                accountId: '',
                accountEmail: '',
                accountName: '',
                loggedInRole: '',
                token: '',
                teamId: '',
                expires: '',
                advisorUsername: unAuthenticatedUser.advisorUsername.get() || '',
                productType: '',
                rolesId: '',
                signupType: ''
            })
        );
        setSubmitting(true)

        try {
            if (isMagicLinkEnabled && magicLink) {
                await createMagicLink({ email, ui_url: APP_ROOT, application_type: SiteSettings.generalGetstartedWithTilesGroup() });
                setSubmitting(false)
                setSelectMagicLink(true);
            } else {
                const accountResource = await authenticateUserInNamespace({
                    email,
                    password,
                });
                const args = {
                    redirectMeta,
                    returnTo,
                    rememberMe,
                    hasIntentUpdate: isNotEmpty(activeIntent),
                    activeIntent,
                    sendNotification,
                    userSpecificIntent: workFlow
                }

                const authentication = new AuthenticationManager(accountResource, args, rememberMe);
                const message = pathOr('', ['message'], accountResource);
                const mobile_ending_with = pathOr('', ['mobile_ending_with'], accountResource);
                setSubmitting(false)
                if (isNotEmpty(message)) {
                    setUserDetails({
                        email,
                        password,
                        rememberMe,
                        submitType,
                        message,
                        mobile_ending_with
                    });
                    setSelectMFA(true);
                    return
                }

                if (accountResource) {
                    authentication.verifyLogin(history);
                    return
                }

                sendNotification({
                    msg: `Login didn't complete. Try again.`,
                    variant: 'warning',
                });

            }
        } catch (errResponse) {
            const result = handleLoginError(errResponse)
            setSubmitting(false)
            handleLoginErrorReason(getLoginErrorData(errResponse) as ErrorResponseType, () => sendNotification({
                msg: result,
                variant: 'error',
            }))
        }
    }, [activeIntent, dispatch, magicLink, setSubmitting, redirectMeta, returnTo, sendNotification, history]);



    const handleClickShowPassword = useCallback(() => {
        setShowPassword(prevShowPassword => !prevShowPassword);
    }, []);

    const handleChange = useCallback(() => {
        setMagicLink(prevMagicLink => !prevMagicLink);
    }, []);

    const toggleModal = useCallback(() => {
        setSelectMagicLink(false);
    }, []);


    const closeAccountLockedModal = () => {
        setAccountLockedData(prev => ({
            ...prev,
            show: false
        }))
    }

    const toggleMFAModal = useCallback(() => {
        setSelectMFA(prevSelectMFA => !prevSelectMFA);
    }, []);

    const handleOTPSubmit = useCallback(async (verificationCode: any) => {
        const { email, rememberMe } = userDetails;
        try {
            setSubmitting(true);
            const accountResource = await verifyOTP({
                email,
                password: verificationCode,
            });
            const args = {
                redirectMeta,
                returnTo,
                rememberMe,
                setSelectMFA,
                sendNotification,
                dispatch
            }
            const authentication = new AuthenticationManager(accountResource, args, rememberMe);
            setSubmitting(false);
            authentication.verifyLogin(history);
        } catch (errResponse) {
            const result = handleLoginError(errResponse);
            setSubmitting(false)
            sendNotification({
                msg: result,
                variant: 'error',
            });
        }
    }, [userDetails, redirectMeta, returnTo, setSelectMFA, sendNotification, dispatch, history]);

    return (
        <>
            <FormControl onSubmit={handleSubmit(onSubmit)} sx={{ justifyContent: 'center', margin: "auto" }}>
                <FormContainer container rowSpacing={2}>
                    <Grid item>
                        <Typography
                            component="h1"
                            variant="h1"
                            fontSize={matchesLg ? 35 : 30}
                            mt={matchesLg ? 5 : 0}
                        >
                            {isEmptyOrNil(activeIntent) ? "Welcome back!" : "Login"}
                        </Typography>
                        <Typography
                            variant="body1"
                            color="basic.grey"
                            whiteSpace='initial'
                        >{isEmptyOrNil(activeIntent) ? "Sign in to view the status or complete the next steps for your application." : "Sign in to complete the next steps for your application."}

                        </Typography>
                    </Grid>
                    <Grid item>
                        <InputLabel sx={{ marginBottom: 0.5 }}>
                            Enter your email address
                        </InputLabel>
                        <Controller
                            name="email"
                            control={control}
                            rules={{
                                required: {
                                    value: true,
                                    message: 'Email is required',
                                },
                                pattern: {
                                    value: EMAIL_VALIDATION_REGEX,
                                    message:
                                        'Enter a valid email',
                                },
                            }}
                            render={({ field: { onChange, value } }) => (
                                <TextField
                                    value={toLower(trim(value))}
                                    onChange={onChange}
                                    error={Boolean(errors?.email)}
                                    helperText={errors?.email?.message}
                                    placeholder="your@email.com"
                                />
                            )}
                        />
                    </Grid>
                    {!magicLink ? (
                        <>
                            <Grid item>
                                <InputLabel htmlFor="password" sx={{ marginBottom: 0.5 }}>
                                    Enter your password
                                </InputLabel>
                                <Controller
                                    name="password"
                                    control={control}
                                    rules={{
                                        required: {
                                            value: true,
                                            message: 'Password is required',
                                        },
                                        minLength: {
                                            value: strongMinCharPassword,
                                            message:
                                                `Minimum password length is ${strongMinCharPassword} characters.`,
                                        },
                                    }}
                                    render={({
                                        field: { onChange, value },
                                        fieldState: { invalid, error },
                                    }) => (
                                        <TextField
                                            onChange={onChange}
                                            value={value}
                                            placeholder="*******"
                                            error={invalid}
                                            helperText={error?.message}
                                            type={showPassword ? 'text' : 'password'}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            aria-label="toggle password visibility"
                                                            onClick={handleClickShowPassword}
                                                            edge="end"
                                                        >
                                                            {showPassword ? (
                                                                <VisibilityOff />
                                                            ) : (
                                                                <Visibility />
                                                            )}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid
                                item
                                container
                                justifyContent="flex-end"
                                alignItems="center"
                            >
                                <Link component={RLink} to={FORGOT_URL}>
                                    <Typography fontSize={14} color="primaryColor">
                                        Forgot password?
                                    </Typography>
                                </Link>
                            </Grid>
                        </>
                    ) : (
                        <Grid ml={-1.5} sx={{ marginTop: 2 }}>
                            <Typography
                                fontSize={14}
                                component="p"
                                lineHeight="initial"
                                className={classes.magicLabel}
                            >
                                After we verify your email, you'll be sent a secure login
                                link to access your account without needing a password.
                            </Typography>
                        </Grid>
                    )}
                    {isEmptyOrNil(activeIntent) && <>
                        {isMagicLinkEnabled && magicLink && <Grid
                            item
                            container
                            justifyContent="flex-end"
                            alignItems="center"
                        >
                            <Link component={RLink} to={FORGOT_URL}>
                                <Typography fontSize={14} color="primaryColor">
                                    Forgot password?
                                </Typography>
                            </Link>
                        </Grid>}
                      {isMagicLinkEnabled &&
                        <Grid
                          ml={-1.5}
                          className={magicLink ? classes.magicCheckBox : ''}
                        >
                          <Checkbox value={magicLink} id="magic-link" onChange={handleChange} />
                          <Typography fontSize={14} component="span" lineHeight="initial" onClick={handleChange}>
                            Use magic link for logging into my account
                          </Typography>
                        </Grid>}
                    </>}
                    <Grid
                        item
                        alignSelf="center"
                        width={matchesMd ? '100%' : 'initial'}
                    >
                        <Button
                            type="submit"
                            text="Login"
                            loading={submitting}
                            styles={{
                                width: matchesMd ? '100%' : '280px',
                            }}
                        />
                    </Grid>
                </FormContainer>
            </FormControl>
            {isMagicLinkEnabled &&
              <MagicLinkModal
                toggleModal={toggleModal}
                selectMagicLink={selectMagicLink}
                control={control}
              />
            }
            <AccountLockedModal
                message={accountLockedData.message}
                show={accountLockedData.show}
                close={closeAccountLockedModal}
            />
            <OTPVerification
                selectMFA={selectMFA}
                toggleMFAModal={toggleMFAModal}
                mobileEndingWith={pathOr('', ['mobile_ending_with'], userDetails)}
                handleOTPSubmit={handleOTPSubmit}
            />
        </>
    );
};
export default compose(withRouter)(LoginContainer);
